import * as React from 'react';
import { forEach } from 'es-toolkit/compat';

import { ScriptLoadState, newScript } from '../../utils/scriptUtils';
import { logger } from '@tock/logging';
import BaseComponent from '../BaseComponent';

export interface ScriptLoaderProps {
  scriptsLoaded: boolean;
  scriptsLoadSuccess: boolean;
}

interface Props {
  children?: React.ReactNode;
  scripts: ScriptsProp;
  async?: boolean;
  retryAttempts: number;
  render?(stateToInject: ScriptLoaderProps): React.ReactNode;
  onComplete?(): void;
}

interface ScriptsProp {
  [id: string]: string;
}

interface Script {
  src: string;
  loaded: ScriptLoadState;
  attempts: number;
}

interface State {
  stateToInject: ScriptLoaderProps;
}

const loadedScripts: { [id: string]: string } = {};

export default class ScriptLoader extends BaseComponent<Props, State> {
  static displayName: string;
  scripts: {
    [id: string]: Script;
  } = {};

  constructor(props: Props) {
    super(props);
    this.state = {
      stateToInject: {
        scriptsLoaded: false,
        scriptsLoadSuccess: false,
      },
    };

    this.attachScriptsToComponent(props.scripts);
    this.state = {
      ...this.state,
      ...this.calculateInjectedProps(),
    };
  }

  componentDidMount() {
    this.fetchScriptIfNeeded();
  }

  componentDidUpdate(prevProps: Props) {
    const newScripts: ScriptsProp = {};
    forEach(this.props.scripts, (src: string, id: string) => {
      if (this.scripts[id] === undefined) {
        newScripts[id] = src;
      }
    });
    this.attachScriptsToComponent(newScripts);
    this.fetchScriptIfNeeded();
  }

  attachScriptsToComponent(scripts: ScriptsProp) {
    forEach(scripts, (src: string, id: string) => {
      if (BROWSER) {
        const node = document.getElementById(id);
        if (node && node.parentNode) {
          loadedScripts[id] = src;
        }
      }

      this.scripts[id] = {
        loaded: loadedScripts[id] ? ScriptLoadState.SUCCESS : ScriptLoadState.INACTIVE,
        attempts: 0,
        src,
      };
    });
  }

  /*
   * Looks for a script that needs fetching and calls it to be fetched
   */
  fetchScriptIfNeeded() {
    forEach(this.scripts, (script: Script, id: string) => {
      if (loadedScripts[id]) {
        return;
      }

      const shouldFetch =
        (script.loaded === ScriptLoadState.FAILED && script.attempts <= this.props.retryAttempts) ||
        script.loaded === ScriptLoadState.INACTIVE;
      if (shouldFetch) {
        logger.debug(`setting ${id} - ${ScriptLoadState.PENDING}`);
        this.scripts[id] = {
          ...this.scripts[id],
          loaded: ScriptLoadState.PENDING,
        };
        this.fetchScript(id);
      }
    });
  }

  /*
   * Calls our script mounting utility with a callback to update state.
   */
  fetchScript(id: string) {
    const key = id;

    const scriptSrc = this.props.scripts[id];
    newScript(scriptSrc, key, (result: ScriptLoadState) => {
      if (result === ScriptLoadState.FAILED) {
        logger.error(`Loading ${id} - ${result}`);
        this.removeScript(key);
      }

      if (result === ScriptLoadState.SUCCESS) {
        loadedScripts[id] = scriptSrc;
      }

      logger.debug(`setting ${id} - ${result}`);
      this.scripts[id] = {
        src: scriptSrc,
        loaded: result,
        attempts: this.scripts[id].attempts + 1,
      };
      this.setState(this.calculateInjectedProps());
    });
  }

  /*
   * Remove a script from the DOM, e.g. if a script load fails.
   */
  removeScript(id: string) {
    const node = document.getElementById(id);
    if (node && node.parentNode) {
      node.parentNode.removeChild(node);
    }
  }

  /*
   * Calculates the stateToInject state values that will be injected into the wrapped
   * component once the script(s) load.
   */
  calculateInjectedProps() {
    let scriptsLoaded = true;
    let scriptsLoadSuccess = true;
    forEach(this.scripts, (script: Script, id: string) => {
      const success = script.loaded === ScriptLoadState.SUCCESS;
      const failure = script.loaded === ScriptLoadState.FAILED;
      const loaded = success || (failure && script.attempts > this.props.retryAttempts);

      scriptsLoaded = scriptsLoaded && loaded;
      scriptsLoadSuccess = scriptsLoadSuccess && success;
    });

    logger.debug(`setting stateToInject`, {
      scriptsLoaded,
      scriptsLoadSuccess,
    });
    if (scriptsLoaded && !this.state.stateToInject.scriptsLoaded && this.props.onComplete) {
      setTimeout(this.props.onComplete, 0);
    }
    return {
      stateToInject: {
        scriptsLoaded,
        scriptsLoadSuccess,
      },
    };
  }

  render() {
    const { children, render } = this.props;
    const { stateToInject } = this.state;

    if (render) {
      return render(stateToInject);
    }

    if (
      children &&
      ((stateToInject.scriptsLoaded && stateToInject.scriptsLoadSuccess) || this.props.async)
    ) {
      return React.Children.only(children);
    }

    return null;
  }
}
